<template>
  <div class="device-management">
    <!-- User Device Management Content -->
    <div class="bg-white rounded-lg shadow-md p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-800">
          <font-awesome-icon icon="mobile-alt" class="mr-2" />
          My Devices
        </h1>
        <div class="flex gap-3">
          <el-button
            type="success"
            size="small"
            @click="showRegisterDialog = true"
            :disabled="loading"
          >
            <font-awesome-icon icon="plus" class="mr-1" />
            Register Device
          </el-button>
          <el-button
            type="warning"
            size="small"
            @click="refreshDevices"
            :loading="loading"
          >
            <font-awesome-icon icon="sync-alt" class="mr-1" />
            Refresh
          </el-button>
        </div>
      </div>

      <!-- Current User Info -->
      <div class="mb-4 p-4 bg-blue-50 rounded-lg">
        <h3 class="text-sm font-semibold text-gray-700 mb-2">Current User Information</h3>
        <div class="text-sm text-gray-600">
          <p><strong>Username:</strong> {{ currentUsername }}</p>
          <p><strong>Total Devices:</strong> {{ devices.length }}</p>
          <p><strong>Primary Device:</strong> {{ primaryDevice ? primaryDevice.deviceName : 'None' }}</p>
        </div>
      </div>
      <!-- Devices Table -->
      <div v-if="devices.length > 0">
        <h3 class="text-lg font-semibold text-gray-700 mb-4">Your Registered Devices</h3>
        <el-table
          :data="devices"
          stripe
          style="width: 100%"
          v-loading="loading"
        >
          <el-table-column
            prop="deviceName"
            label="Device Name"
            width="200"
          >
            <template slot-scope="scope">
              <div class="flex flex-col">
                <!-- <font-awesome-icon 
                  :icon="getDeviceIcon(scope.row.deviceType)" 
                  class="text-blue-500" 
                /> -->
                {{ scope.row.deviceName }}
                <div>
                  <el-tag 
                    v-if="scope.row.isPrimary" 
                    size="mini" 
                    type="success" 
                  >
                    Primary
                  </el-tag>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            prop="deviceType"
            label="Type"
            width="100"
          />

          <el-table-column
            prop="osVersion"
            label="OS Version"
            width="120"
          />

          <el-table-column
            prop="appVersion"
            label="App Version"
            width="120"
          />

          <el-table-column
            prop="isActive"
            label="Status"
            width="100"
          >
            <template slot-scope="scope">
              <el-tag 
                :type="scope.row.isActive ? 'success' : 'danger'" 
                size="mini"
              >
                {{ scope.row.isActive ? 'Active' : 'Inactive' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column
            prop="lastUsedAt"
            label="Last Used"
            width="160"
          >
            <template slot-scope="scope">
              {{ formatDate(scope.row.lastUsedAt) }}
            </template>
          </el-table-column>

          <el-table-column
            prop="createdAt"
            label="Registered"
            width="160"
          >
            <template slot-scope="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>

          <el-table-column
            label="Actions"
            width="215"
          >
            <template slot-scope="scope">
              <div class="flex space-x-2">
                <el-button
                  v-if="!scope.row.isPrimary"
                  type="primary"
                  @click="setPrimaryDevice(scope.row)"
                  :disabled="loading"
                  size="mini"
                >
                  Set Primary
                </el-button>
                <el-button
                  type="info"
                  @click="updateLastUsed(scope.row)"
                  :disabled="loading"
                  size="mini"
                >
                  Update Usage
                </el-button>
                <el-button
                  type="danger"
                  @click="confirmRemoveDevice(scope.row)"
                  :disabled="loading"
                  size="mini"
                >
                  Remove
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- No devices message -->
      <div v-else-if="!loading" class="text-center py-8">
        <font-awesome-icon icon="mobile-alt" class="text-6xl text-gray-300 mb-4" />
        <p class="text-gray-500 text-lg">No devices registered</p>
        <p class="text-gray-400 text-sm mb-4">Register your first device to get started</p>
        <el-button type="primary" @click="showRegisterDialog = true">
          Register Device
        </el-button>
      </div>
    </div>

    <!-- Admin Section (only shown for admin users) -->
    <div v-if="isAdmin" class="bg-white rounded-lg shadow-md p-6">
      <h2 class="text-xl font-bold text-gray-800 mb-4">
        <font-awesome-icon icon="cogs" class="mr-2" />
        Admin Functions
      </h2>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <el-card class="text-center">
          <div class="text-2xl font-bold text-blue-600 mb-2">{{ totalDevices }}</div>
          <div class="text-gray-600">Total Registered Devices</div>
        </el-card>
        <el-card class="text-center">
          <div class="text-2xl font-bold text-green-600 mb-2">{{ activeDevices }}</div>
          <div class="text-gray-600">Active Devices</div>
        </el-card>
      </div>

      <div class="flex gap-3 mb-4">
        <el-button
          type="warning"
          @click="cleanupStaleDevices"
          :loading="loading"
        >
          <font-awesome-icon icon="trash" class="mr-1" />
          Cleanup Stale Devices
        </el-button>
        <el-input
          v-model="searchUsername"
          placeholder="Enter username to search"
          class="w-64"
        />
        <el-button
          type="info"
          @click="searchUserDevices"
          :disabled="!searchUsername"
        >
          <font-awesome-icon icon="search" class="mr-1" />
          Search User Devices
        </el-button>
      </div>
    </div>

    <!-- Register Device Dialog -->
    <el-dialog
      title="Register New Device"
      :visible.sync="showRegisterDialog"
      width="500px"
    >
      <el-form :model="newDevice" :rules="deviceRules" ref="deviceForm" label-width="120px">
        <el-form-item label="FCM Token" prop="fcmToken">
          <el-input
            v-model="newDevice.fcmToken"
            placeholder="Enter FCM token"
            type="textarea"
            :rows="3"
          />
        </el-form-item>
        <el-form-item label="Device Name" prop="deviceName">
          <el-input
            v-model="newDevice.deviceName"
            placeholder="e.g., John's iPhone 12"
          />
        </el-form-item>
        <el-form-item label="Device Type" prop="deviceType">
          <el-select v-model="newDevice.deviceType" placeholder="Select device type">
            <el-option label="Android" value="ANDROID" />
            <el-option label="iOS" value="IOS" />
            <el-option label="Web" value="WEB" />
            <el-option label="Desktop" value="DESKTOP" />
          </el-select>
        </el-form-item>
        <el-form-item label="OS Version" prop="osVersion">
          <el-input v-model="newDevice.osVersion" placeholder="e.g., iOS 15.0, Android 11" />
        </el-form-item>
        <el-form-item label="App Version" prop="appVersion">
          <el-input v-model="newDevice.appVersion" placeholder="e.g., 1.0.0" />
        </el-form-item>
        <el-form-item>
          <el-checkbox v-model="newDevice.isPrimary">Set as primary device</el-checkbox>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="showRegisterDialog = false">Cancel</el-button>
        <el-button type="primary" @click="registerDevice" :loading="loading">
          Register Device
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'DeviceManagementHome',
  data() {
    return {
      showRegisterDialog: false,
      searchUsername: '',
      newDevice: {
        fcmToken: '',
        deviceName: '',
        deviceType: '',
        osVersion: '',
        appVersion: '',
        isPrimary: false
      },
      deviceRules: {
        fcmToken: [
          { required: true, message: 'FCM token is required', trigger: 'blur' },
          { min: 10, message: 'FCM token must be at least 10 characters', trigger: 'blur' }
        ],
        deviceName: [
          { required: true, message: 'Device name is required', trigger: 'blur' }
        ],
        deviceType: [
          { required: true, message: 'Device type is required', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    devices() {
      return this.$store.getters['device/getDevices']
    },
    loading() {
      return this.$store.getters['device/isLoading']
    },
    error() {
      return this.$store.getters['device/getError']
    },
    message() {
      return this.$store.getters['device/getMessage']
    },
    currentUsername() {
      return this.$store.state.info?.sub || 'Unknown'
    },
    isUserAdmin() {
      // Check if user is admin based on username or roles
      if (this.currentUsername.toLowerCase().includes('admin')) {
        return true;
      }
      // Check if user has admin role in JWT token
      const roles = this.$store.state.userRoles;
      if (roles && Array.isArray(roles)) {
        return roles.some(role => 
          role.authority === 'ROLE_ADMIN' || 
          role.authority === 'ADMIN' ||
          role === 'ROLE_ADMIN' ||
          role === 'ADMIN'
        );
      }
      return false;
    },
    isAdmin() {
      // Alias for isUserAdmin for backward compatibility
      return this.isUserAdmin
    },
    primaryDevice() {
      // Ensure devices is an array before using find
      const devicesList = Array.isArray(this.devices) ? this.devices : []
      return devicesList.find(device => device.isPrimary)
    },
    totalDevices() {
      return Array.isArray(this.devices) ? this.devices.length : 0
    },
    activeDevices() {
      const devicesList = Array.isArray(this.devices) ? this.devices : []
      return devicesList.filter(device => device.isActive).length
    }
  },
  watch: {
    error(newError) {
      if (newError) {
        this.$message.error(newError)
      }
    },
    message(newMessage) {
      if (newMessage) {
        this.$message.success(newMessage)
      }
    }
  },
  mounted() {
    this.loadUserDevices()
  },
  methods: {
    async loadUserDevices() {
      try {
        await this.$store.dispatch('device/getUserDevices', this.currentUsername)
      } catch (error) {
        console.error('Error loading user devices:', error)
      }
    },

    async refreshDevices() {
      await this.loadUserDevices()
    },

    async registerDevice() {
      try {
        await this.$refs.deviceForm.validate()
        
        const deviceData = {
          action: 'REGISTER',
          username: this.currentUsername,
          fcmToken: this.newDevice.fcmToken, // This will be mapped to idDevice in store
          deviceName: this.newDevice.deviceName,
          deviceType: this.newDevice.deviceType
        }

        console.log('Registering device:', deviceData)
        await this.$store.dispatch('device/manageDevice', deviceData)
        
        this.showRegisterDialog = false
        this.resetForm()
        await this.loadUserDevices()
        
      } catch (error) {
        console.error('Error registering device:', error)
      }
    },

    async setPrimaryDevice(device) {
      try {
        const deviceData = {
          action: 'SET_PRIMARY',
          username: this.currentUsername,
          fcmToken: device.fcmToken || device.idDevice // Support both field names
        }

        console.log('Setting primary device:', deviceData)
        await this.$store.dispatch('device/manageDevice', deviceData)
        await this.loadUserDevices()
        
      } catch (error) {
        console.error('Error setting primary device:', error)
      }
    },

    async updateLastUsed(device) {
      try {
        await this.$store.dispatch('device/updateLastUsed', device.fcmToken)
        await this.loadUserDevices()
      } catch (error) {
        console.error('Error updating last used:', error)
      }
    },

    confirmRemoveDevice(device) {
      this.$confirm(
        `Are you sure you want to remove device "${device.deviceName}"?`,
        'Confirm Remove',
        {
          confirmButtonText: 'Remove',
          cancelButtonText: 'Cancel',
          type: 'warning',
        }
      ).then(() => {
        this.removeDevice(device)
      }).catch(() => {
        // User cancelled
      })
    },

    async removeDevice(device) {
      try {
        const deviceData = {
          action: 'REMOVE',
          username: this.currentUsername,
          fcmToken: device.fcmToken || device.idDevice // Support both field names
        }

        console.log('Removing device:', deviceData)
        await this.$store.dispatch('device/manageDevice', deviceData)
        await this.loadUserDevices()
        
      } catch (error) {
        console.error('Error removing device:', error)
      }
    },

    async cleanupStaleDevices() {
      try {
        const result = await this.$prompt(
          'Enter number of days for stale device threshold',
          'Cleanup Stale Devices',
          {
            confirmButtonText: 'Cleanup',
            cancelButtonText: 'Cancel',
            inputPattern: /^\d+$/,
            inputErrorMessage: 'Please enter a valid number',
            inputValue: '30'
          }
        )

        await this.$store.dispatch('device/cleanupStaleDevices', parseInt(result.value))
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error cleaning up stale devices:', error)
        }
      }
    },

    async searchUserDevices() {
      if (!this.searchUsername) return

      try {
        await this.$store.dispatch('device/getUserDevices', this.searchUsername)
      } catch (error) {
        console.error('Error searching user devices:', error)
      }
    },

    resetForm() {
      this.newDevice = {
        fcmToken: '',
        deviceName: '',
        deviceType: '',
        osVersion: '',
        appVersion: '',
        isPrimary: false
      }
      if (this.$refs.deviceForm) {
        this.$refs.deviceForm.resetFields()
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'Never'
      return moment(dateString).format('DD/MM/YYYY HH:mm')
    },

    getDeviceIcon(deviceType) {
      const icons = {
        'ANDROID': 'mobile-alt',
        'IOS': 'mobile-alt',
        'WEB': 'desktop',
        'DESKTOP': 'laptop'
      }
      return icons[deviceType] || 'mobile-alt'
    }
  }
}
</script>

<style scoped>
.device-management {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.el-card {
  margin-bottom: 10px;
}

.dialog-footer {
  text-align: right;
}
</style>
